import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAnalyticsSafeData, selectUser, waitForUserSelf } from './user-profile.selectors';
import getCookie, { COOKIE_MH_TOKEN } from '../../common/cookies';
import { clearLoginCustomerState, loadCustomer } from '../customer/customer';
import emit from '../../common/events';
import { StoreState } from '../../store/store.interface';
import { ActionStatus } from '../../common/constants/action-status';
import { FetchUserParams } from './user-profile.interface';
import { getUserProfileApiCall } from './api/get-user-profile';
import { getAppFlow, getCartPageUrl, isHEInstructor } from './utils/user-profile.util';
import { AccountPageAppFlow } from '../my-account/app-flow.enum';
import { wait } from '../../common/utils/util';
import { handleApiError, ApiError } from '../../common/utils/api-utils';

/*----- DECLARE THUNKS ------*/
/**
 * Validates the user JWT and gets the user profile.
 */
export const fetchUserProfile = createAsyncThunk(
  'userProfile/fetchUserProfileDetails',
  async (_params: FetchUserParams | undefined, thunkAPI) => {
    if(getAppFlow() === AccountPageAppFlow.Create) {
      // In case of Create flow we call UserSelf api to retrive MH_TOKEN
      try{
        await waitForUserSelf();
        // we are retring to read MH_TOKEN from cookie store sometimes it take time to update the cookie store 
        for (let i = 0; i < 6; i++) {
          if(getCookie(COOKIE_MH_TOKEN)) {
            break;
          } else {
            await wait(500);
          }
        }
      } catch(e) {
        console.error("[only valid for my account create flow] user self api called failed", e);
      }
    }

    try {
      const hasMHTokenCookie = getCookie(COOKIE_MH_TOKEN);
      if (!hasMHTokenCookie) {
        thunkAPI.dispatch(clearLoginCustomerState());
        return null;
      }

      const userInfo = await getUserProfileApiCall(_params?.checkSelfOptOut);
      const userDetailsForAnalytics = getAnalyticsSafeData(userInfo);
      await thunkAPI.dispatch(loadCustomer());

      emit({
        name: 'user-details',
        detail: userDetailsForAnalytics,
      });

      return userInfo;
    } catch (error) {
      console.error(error);
      if (error instanceof ApiError) {
        handleApiError(error);
      }
      return thunkAPI.rejectWithValue(error);
    } finally {
      if (new URL(location.href).pathname == '/checkout/cart') {
        const user = selectUser(thunkAPI.getState() as StoreState);
        if (isHEInstructor(user)) {
          const urlParams = new URLSearchParams(location.search);
          const redirectUrl = new URL(getCartPageUrl(user));
          urlParams.forEach(function (value, key) {
            redirectUrl.searchParams.set(key, value);
          });
          location.href = redirectUrl.toString();
        }
      }
    }
  },
  {
    condition: (_data: FetchUserParams | undefined, { getState }) => {
      const state = getState() as StoreState;
      return [ActionStatus.idle].includes(state.userProfileState.userProfileResponse.status) || !!_data?.forceUpdate;
    },
  }
);

